import {CA} from '../../analytics/Analytics'
import {AjaxDataTypes} from '../../../../../shared/AjaxDataTypes'
import {ABTestShareGameResultsAnalytics, AnalyticEventCategory, AnalyticEventType, AnalyticEventValue} from '../../../../../shared/entity_datas/EntityDatas'
import {UIEndGameShareImage} from '../../ui_share_image/UIEndGameShareImage'
import {UIEndGameTitleImage} from '../../ui_share_image/UIEndGameTitleImage'
import {UIGameManager} from '../UIGameManager'
import {UIGameEndController} from './UIGameEndController'
import {ViewPopupShareImage} from '../../ui_popup/ViewPopupShareImage'
import {UIUtilsEjsVariables} from '../../UIUtilsEjsVariables'
import {ClientUserState} from '../../ClientUserState'

export class UIGameEndShareButtonController {
    static shareImageDiscord?: (imageFile: File) => void
    private readonly uiGameManager: UIGameManager
    private readonly gameEndController: UIGameEndController

    private readonly shareImage: UIEndGameShareImage
    private readonly titleImage?: UIEndGameTitleImage

    private shareImagePopup: ViewPopupShareImage

    constructor(uiGameManager: UIGameManager, gameEndController: UIGameEndController) {
        this.uiGameManager = uiGameManager
        this.gameEndController = gameEndController

        this.shareImage = new UIEndGameShareImage(this.uiGameManager, this.gameEndController.endGameState)
        if(this.gameEndController.endGamePlayerState?.title != null) {
            this.titleImage = new UIEndGameTitleImage(this.uiGameManager, this.gameEndController.endGamePlayerState.title, this.gameEndController.endGameState)
        }

        if(this.shouldShowShareGameResultsPopupAutomaticallyOnGameEnd()) {
            const currentPlayerColor = this.uiGameManager.gameController.myColor

            if(ClientUserState.abTests.END_GAME_SHARE_RESULTS == 'GROUP_A_1ST_PLACE') {
                if(this.gameEndController.endGameState.players[currentPlayerColor]?.rank == 1) this.createShareImagePopupIfNotExist(true)
                return
            }
            if(ClientUserState.abTests.END_GAME_SHARE_RESULTS == 'GROUP_B_2ND_PLACE') {
                if(this.gameEndController.endGameState.players[currentPlayerColor]?.rank == 2) this.createShareImagePopupIfNotExist(true)
                return
            }
            if(ClientUserState.abTests.END_GAME_SHARE_RESULTS == 'GROUP_C_3RD_PLACE') {
                if(this.gameEndController.endGameState.players[currentPlayerColor]?.rank == 3) this.createShareImagePopupIfNotExist(true)
                return
            }
            if(ClientUserState.abTests.END_GAME_SHARE_RESULTS == 'GROUP_D_4TH_PLACE') {
                if(this.gameEndController.endGameState.players[currentPlayerColor]?.rank == 4) this.createShareImagePopupIfNotExist(true)
                return
            }
        }
    }

    onClickShare() {
        this.createShareImagePopupIfNotExist()

        UIGameEndShareButtonController.sendShareButtonClickAnalytic()
    }

    private createShareImagePopupIfNotExist(delay: boolean = false) {
        if(this.isPopupRemovedOrDoesNotExist()) {
            if(delay) {
                setTimeout(() => this.createShareImagePopupIfNotExist(), 3000)
                return
            }

            this.shareImagePopup = this.titleImage ? new ViewPopupShareImage(this.titleImage, this.shareImage, true) : new ViewPopupShareImage(this.shareImage)

            if(UIGameEndShareButtonController.shareImageDiscord) {
                const shareAction = async(image: UIEndGameShareImage) => {
                    const imageFile = await image.getImageFile()
                    if(imageFile == undefined) return

                    UIGameEndShareButtonController.shareImageDiscord?.(imageFile)

                    UIGameEndShareButtonController.sendDiscordShareAnalytic()

                    const floatValuePopupDisplayed = UIGameEndShareButtonController.getABTestAnalytic(ClientUserState.abTests.END_GAME_SHARE_RESULTS, true)
                    UIGameEndShareButtonController.sendABTestAnalytic(floatValuePopupDisplayed)
                }

                this.shareImagePopup.setShareAction(shareAction)
            }

            const floatValuePrePopupDisplayed = UIGameEndShareButtonController.getABTestAnalytic(ClientUserState.abTests.END_GAME_SHARE_RESULTS, false)
            UIGameEndShareButtonController.sendABTestAnalytic(floatValuePrePopupDisplayed)
        }
    }

    private isPopupRemovedOrDoesNotExist(): boolean {
        if(this.shareImagePopup == undefined) return true
        if(this.shareImagePopup.isPopupRemoved()) return true
        return false
    }

    private shouldShowShareGameResultsPopupAutomaticallyOnGameEnd(): boolean {
        if(!UIUtilsEjsVariables.isUserOnDiscord) return false
        if(this.uiGameManager.gameController.isReplay) return false
        return true
    }

    private static sendShareButtonClickAnalytic() {
        const endGameShareEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareButton,
        }
        void CA.sendAnalyticEventOnce(endGameShareEvent)
    }

    private static getABTestAnalytic(abTestGroup: string | undefined, isDiscordPopup: boolean): ABTestShareGameResultsAnalytics {
        if(isDiscordPopup) {
            switch(abTestGroup) {
                case 'GROUP_A_1ST_PLACE': return ABTestShareGameResultsAnalytics.GroupAPopupDisplayed
                case 'GROUP_B_2ND_PLACE': return ABTestShareGameResultsAnalytics.GroupBPopupDisplayed
                case 'GROUP_C_3RD_PLACE': return ABTestShareGameResultsAnalytics.GroupCPopupDisplayed
                case 'GROUP_D_4TH_PLACE': return ABTestShareGameResultsAnalytics.GroupDPopupDisplayed
                default: return ABTestShareGameResultsAnalytics.DefaultPopupDisplayed
            }
        }

        switch(abTestGroup) {
            case 'GROUP_A_1ST_PLACE': return ABTestShareGameResultsAnalytics.GroupAPrePopupDisplayed
            case 'GROUP_B_2ND_PLACE': return ABTestShareGameResultsAnalytics.GroupBPrePopupDisplayed
            case 'GROUP_C_3RD_PLACE': return ABTestShareGameResultsAnalytics.GroupCPrePopupDisplayed
            case 'GROUP_D_4TH_PLACE': return ABTestShareGameResultsAnalytics.GroupDPrePopupDisplayed
            default: return ABTestShareGameResultsAnalytics.DefaultPrePopupDisplayed
        }
    }

    private static sendABTestAnalytic(floatValue: ABTestShareGameResultsAnalytics) {
        const abTestEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.ABTestShareGameResults,
            floatValue: floatValue,
        }
        void CA.sendAnalyticEventOnce(abTestEvent)
    }

    private static sendDiscordShareAnalytic() {
        const discordShareClickEvent: AjaxDataTypes.AnalyticData = {
            type: AnalyticEventType.EndGameShareButton,
            category: AnalyticEventCategory.Virality,
            floatValue: AnalyticEventValue.EndGameShareDiscordButton,
        }
        void CA.sendAnalyticEventOnce(discordShareClickEvent)
    }
}
